import { ElementType, PropsWithChildren, ReactNode } from 'react';

import { SxProps, Theme } from '@mui/material/styles';

import { Box } from '../box/box';
import { useTheme } from '../styles/styles';
import { getDialogFieldsSx } from './dialog-fields';

// Spread into a Box's sx, or use <RedesignCard>.
export const cardSurfaceSx = (theme: Theme) => ({
  borderRadius: '16px',
  background: theme.background.white,
  boxShadow: theme.custom?.cardShadow,
});

const cardTitleTextSx = (theme: Theme) => ({
  fontSize: '16px',
  fontWeight: 650,
  color: theme.custom?.brandNavy,
});

export const cardTitleSx = (theme: Theme) => ({
  ...cardTitleTextSx(theme),
  lineHeight: '22.4px',
  letterSpacing: '-0.08px',
});

type SxArg = SxProps<Theme>;
const toArray = (sx?: SxArg) =>
  (Array.isArray(sx) ? sx : sx ? [sx] : []) as ReadonlyArray<
    Exclude<SxArg, ReadonlyArray<unknown>>
  >;

export const RedesignCard = ({
  component = 'section',
  sx,
  children,
}: PropsWithChildren<{ component?: ElementType; sx?: SxArg }>) => {
  const theme = useTheme();
  return (
    <Box component={component} sx={[cardSurfaceSx(theme), ...toArray(sx)]}>
      {children}
    </Box>
  );
};

// A card's title row with optional controls on the right. `table`: the table
// cards' header; `section`: the settings cards' header (e.g. Preferences).
export const RedesignCardHeader = ({
  title,
  right,
  variant = 'section',
  titleComponent = 'h2',
  sx,
}: {
  title: ReactNode;
  right?: ReactNode;
  variant?: 'table' | 'section';
  titleComponent?: ElementType;
  sx?: SxArg;
}) => {
  const theme = useTheme();
  const table = variant === 'table';
  return (
    <Box
      sx={[
        {
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: table ? '16px 16px 14px 20px' : '14px 20px',
          borderBottom: `1px solid ${theme.custom?.tableTitleDivider}`,
        },
        ...toArray(sx),
      ]}
    >
      <Box
        component={titleComponent}
        sx={{
          margin: 0,
          flex: 1,
          minWidth: 0,
          ...(table
            ? cardTitleSx(theme)
            : {
                fontSize: '15px',
                lineHeight: '22px',
                fontWeight: 600,
                color: theme.custom?.brandNavy,
              }),
        }}
      >
        {title}
      </Box>
      {right}
    </Box>
  );
};

export const SectionCard = ({
  title,
  action,
  children,
}: PropsWithChildren<{ title: string; action?: ReactNode }>) => {
  const theme = useTheme();
  return (
    <RedesignCard sx={{ minWidth: 0 }}>
      <RedesignCardHeader title={title} right={action} />
      <Box
        sx={{
          padding: '16px 20px 18px',
          display: 'grid',
          gap: '14px',
          ...getDialogFieldsSx(theme),
        }}
      >
        {children}
      </Box>
    </RedesignCard>
  );
};

export const CardHint = ({ children }: PropsWithChildren) => {
  const theme = useTheme();
  return (
    <Box
      component="p"
      sx={{
        margin: 0,
        fontSize: '12.5px',
        lineHeight: '18px',
        color: theme.custom?.tableHeaderText,
      }}
    >
      {children}
    </Box>
  );
};

export const LabeledField = ({
  label,
  children,
}: PropsWithChildren<{ label: string }>) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'grid', gap: '5px', minWidth: 0 }}>
      <Box
        component="span"
        sx={{
          fontSize: '12.5px',
          lineHeight: '18px',
          color: theme.custom?.headerInfoText,
        }}
      >
        {label}
      </Box>
      {children}
    </Box>
  );
};

export const pageButtonSx = {
  height: 36,
  minHeight: 36,
  padding: '0 14px',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 400,
  boxShadow: 'none',
  whiteSpace: 'nowrap',
};

export const pageScrollbarSx = (theme: Theme) => ({
  scrollbarWidth: 'auto',
  scrollbarColor: 'auto',
  '&::-webkit-scrollbar': { width: '8px', height: '8px' },
  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.custom?.scrollThumb,
    borderRadius: '10px',
    border: '2px solid transparent',
    backgroundClip: 'content-box',
  },
});

export const CenteredPage = ({ children }: PropsWithChildren) => (
  <Box
    sx={(theme) => ({
      height: { sm: '100%' },
      overflowY: { sm: 'auto' },
      scrollbarGutter: 'stable both-edges',
      ...pageScrollbarSx(theme),
    })}
  >
    <Box
      sx={{
        maxWidth: 1180,
        margin: '0 auto',
        padding: { xs: '16px 12px 32px', md: '26px 28px 40px' },
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gap: '16px',
      }}
    >
      {children}
    </Box>
  </Box>
);

export const PageTitleHeader = ({
  title,
  subtitle,
  right,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
}) => {
  const theme = useTheme();
  const text = (
    <>
      <Box
        component="h1"
        sx={{
          margin: 0,
          fontSize: '24px',
          lineHeight: '32px',
          fontWeight: 700,
          letterSpacing: '-0.01em',
          color: theme.custom?.brandNavy,
        }}
      >
        {title}
      </Box>
      {subtitle && (
        <Box
          component="p"
          sx={{
            margin: '4px 0 0',
            fontSize: '14px',
            lineHeight: '20px',
            color: theme.custom?.headerInfoText,
          }}
        >
          {subtitle}
        </Box>
      )}
    </>
  );
  if (!right) return <Box component="header">{text}</Box>;
  return (
    <Box
      component="header"
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        flexWrap: 'wrap',
        gap: '12px',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>{text}</Box>
      {right}
    </Box>
  );
};
